// Dane doradcy maszyn (@theme/advisor-data): czyste funkcje wspólne dla doradcy, zajawki, panelu i doboru zestawów.
// Przeniesione z modułu sklepu: machine-advisor.js (pobieranie katalogu, widoczność, wiązanie produktów, kod zestawu SE-XXXX).
// Logika i formaty danych bez zmian (ARCHITEKTURA §11, D8); kod zestawu musi się zgadzać bit w bit z wydanymi kodami.
//
// API (opis w docs/build/J1a/RAPORT.md):
//   DRAFT_KEY, readDraft()
//   loadJson(url), loadCatalog(url?), loadAccessories(url?), assetUrl(file, width?)
//   visibilityOf(catalog, hasProduct?), applyVisibility(catalog, hasProduct?), shownItem(accessories, code)
//   numberOf(value), slug(text), loadShopProducts(collection?, { fresh }?), linkShopProducts(machines, products)
//   pickPreviewMachines(catalog, categoryId), firstStep(catalog, categoryId), advisorUrl(params?, page?)
//   kitOptions(accessories, machineId), kitFingerprint(options), encodeKitCode(no, options, picked),
//   decodeKitCode(text, machines, accessories)

/** Klucz szkicu panelu w localStorage: `{ base, savedAt, catalog, accessories }`. */
export const DRAFT_KEY = 'se-advisor-draft';

const DEFAULT_PAGE = '/pages/konfigurator-maszyn';
const DEFAULT_COLLECTION = 'aplikacja-konfigurator';
const MAX_PAGES = 40;
const PRODUCTS_TTL = 10 * 60 * 1000;

// Wersjonowane adresy z layoutu, gdy są; inaczej pliki z tego samego folderu assets co moduł.
const assets = window.Theme?.assets || {};
const CATALOG_URL = assets.machinesCatalog || new URL('machines-catalog.json', import.meta.url).href;
const ACCESSORIES_URL = assets.machinesAccessories || new URL('machines-accessories.json', import.meta.url).href;

/** Szkic z panelu, tylko gdy adres strony ma `?podglad=szkic` (dane widzi wyłącznie ta przeglądarka). */
export function readDraft() {
  if (new URLSearchParams(location.search).get('podglad') !== 'szkic') return null;
  try {
    const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null');
    return draft && draft.catalog && draft.accessories ? draft : null;
  } catch {
    return null;
  }
}

const jsonCache = new Map();
const getJson = (url) =>
  fetch(url).then((response) => {
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  });

/**
 * JSON pobierany raz na stronę. `asset_url` jest bezprotokołowy, a pod `theme dev` (http) CDN nie odsyła CORS —
 * dlatego po błędzie ponawiamy po https.
 */
export function loadJson(url) {
  if (!jsonCache.has(url)) {
    const https = url.startsWith('//') ? `https:${url}` : url;
    const request = getJson(url)
      .catch((error) => (https === url ? Promise.reject(error) : getJson(https)))
      .catch((error) => {
        jsonCache.delete(url);
        throw error;
      });
    jsonCache.set(url, request);
  }
  return jsonCache.get(url);
}

/** Katalog maszyn (albo katalog szkicu przy `?podglad=szkic`). Obiekt współdzielony — nie modyfikować. */
export function loadCatalog(url = CATALOG_URL) {
  const draft = readDraft();
  return draft ? Promise.resolve(draft.catalog) : loadJson(url);
}

/** Osprzęt `{ items: {kod: {k, n, d, no, hidden?}}, machines: {id: [{t, c: [kody]}]} }` (albo szkic). */
export function loadAccessories(url = ACCESSORIES_URL) {
  const draft = readDraft();
  return draft ? Promise.resolve(draft.accessories) : loadJson(url);
}

/**
 * Adres zdjęcia maszyny lub serii (pole `img` katalogu) — pliki leżą w assets motywu obok modułu.
 * Z `width` (px) — wersja zmniejszona przez CDN; adresy spoza assets motywu bez zmian.
 */
export function assetUrl(file, width) {
  const url = new URL(file, import.meta.url);
  if (width) {
    const module = new URL(import.meta.url);
    // CDN Shopify skaluje obraz z assets parametrem width tylko obok drugiego parametru — jak w adresach image_url (v=).
    if (new URL('./', url).href === new URL('./', module).href) {
      url.searchParams.set('v', module.searchParams.get('v') || '1');
      url.searchParams.set('width', String(width));
    }
  }
  return url.href;
}

/**
 * Powód ukrycia maszyny: '' (widoczna), 'machine', 'brand', 'series', 'cats' albo 'product'.
 * `hasProduct` podawać tylko po wczytaniu produktów — inaczej „tylko z produktem” ukryłoby cały katalog.
 */
export function visibilityOf(data, hasProduct) {
  const ids = (rows) => new Set((rows || []).filter((row) => row.hidden).map((row) => row.id));
  const brands = ids(data.brands);
  const cats = ids(data.categories);
  const series = ids(data.series);
  const onlyWithProduct = Boolean(hasProduct && data.visibility && data.visibility.onlyWithProduct);

  return (m) => {
    if (m.hidden) return 'machine';
    if (brands.has(m.brand)) return 'brand';
    if (series.has(`${m.brand}::${m.series}`)) return 'series';
    if ((m.cats || []).length && m.cats.every((c) => cats.has(c))) return 'cats';
    if (onlyWithProduct && !hasProduct(m)) return 'product';
    return '';
  };
}

/** Kopia katalogu z tym, co widzi klient. Nic nie jest usuwane z danych (flagi `hidden`, NT-20). */
export function applyVisibility(data, hasProduct) {
  const reason = visibilityOf(data, hasProduct);
  const hiddenCats = new Set((data.categories || []).filter((c) => c.hidden).map((c) => c.id));
  const withoutHiddenCats = (list) => (list || []).filter((c) => !hiddenCats.has(c));

  const machines = data.machines
    .filter((m) => !reason(m))
    .map((m) => (hiddenCats.size ? { ...m, cats: withoutHiddenCats(m.cats) } : m));

  const perSeries = new Map();
  for (const m of machines) {
    const key = `${m.brand}::${m.series}`;
    perSeries.set(key, (perSeries.get(key) || 0) + 1);
  }
  const usedCats = new Set(machines.flatMap((m) => m.cats || []));

  return {
    ...data,
    machines,
    brands: (data.brands || []).filter((b) => !b.hidden && machines.some((m) => m.brand === b.id)),
    categories: (data.categories || []).filter((c) => !c.hidden && usedCats.has(c.id)),
    series: (data.series || [])
      .filter((row) => perSeries.get(row.id))
      .map((row) => ({ ...row, count: perSeries.get(row.id), cats: withoutHiddenCats(row.cats) })),
  };
}

/** Akcesorium, które klient może zobaczyć (istnieje i nie jest ukryte), albo null. */
export function shownItem(accessories, code) {
  const item = accessories && accessories.items ? accessories.items[code] : null;
  return item && !item.hidden ? item : null;
}

/** Numer z metapola `custom.nr_konfigurator`: same cyfry, bez zer wiodących ('' gdy brak). */
export function numberOf(value) {
  const digits = String(value == null ? '' : value).replace(/[^0-9]/g, '');
  return digits ? String(parseInt(digits, 10)) : '';
}

/** Małe litery bez diakrytyków i znaków spoza a–z0–9 (dopasowanie nazw). */
export function slug(text) {
  return String(text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

const productRequests = new Map();

/**
 * Produkty kolekcji doradcy z widoku `?view=konfigurator` (po 50 na stronę, maks. 40 stron):
 * `[{ id, no, title, url, variant, available, price, cents }]`. Bez nagłówka `Accept: application/json` —
 * z nim Shopify ignoruje `view`. Pamięć w obrębie strony i w sessionStorage (10 min); `fresh` je pomija.
 * Błąd strony przerywa pobieranie: lista ma wtedy `partial === true` (i nie trafia do sessionStorage).
 */
export function loadShopProducts(collection = DEFAULT_COLLECTION, { fresh = false } = {}) {
  if (!collection) return Promise.resolve([]);
  const base = collection.includes('/') ? collection : `/collections/${encodeURIComponent(collection)}`;
  const key = `advisor-products:${base}`;
  if (!fresh && productRequests.has(key)) return productRequests.get(key);

  const request = (async () => {
    if (!fresh) {
      try {
        const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (saved && Date.now() - saved.time < PRODUCTS_TTL) return saved.products;
      } catch {}
    }
    const products = [];
    for (let page = 1; page <= MAX_PAGES; page += 1) {
      let data;
      try {
        const response = await fetch(`${base}?view=konfigurator&page=${page}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        data = JSON.parse(await response.text());
      } catch {
        // Niepełnej listy nie pamiętamy: następne wywołanie na tej stronie ponowi pobieranie.
        // (Tu `request` jest już przypisany — catch wykonuje się po pierwszym await.)
        if (productRequests.get(key) === request) productRequests.delete(key);
        products.partial = true;
        return products;
      }
      products.push(...(data.products || []));
      if (!data.next) break;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify({ time: Date.now(), products }));
    } catch {}
    return products;
  })();
  productRequests.set(key, request);
  return request;
}

const MACHINE_WORDS = ['urzadzen', 'automat', 'ekstraktor', 'odkurzacz', 'szorowarka', 'zamiatarka', 'maszyna', 'myjka',
  'generator', 'parownica', 'pianotwor', 'froterka'];
const PART_WORDS = ['wezyk', 'uszczelka', 'plywak', 'korpus', 'gabka', 'filtr', 'ssawa', 'dysza', 'kolko', 'podstawa',
  'pokrywa', 'sruba', 'sprezyna', 'listwa', 'szczotka', 'pad', 'wklad', 'worek', 'uchwyt', 'zaslepka', 'silnik', 'pompa'];

/**
 * Wiąże produkty sklepu z maszynami: ustawia `machine.shop` (modyfikuje przekazane obiekty — podawać kopie).
 * Pewne jest tylko powiązanie po numerze (pierwszy produkt wygrywa); bez numeru zgadujemy po kodzie w tytule,
 * gdy kod ma ≥ 5 znaków z literą, tytuł nazywa maszynę i nie nazywa części (najdłuższy kod wygrywa).
 * Zwraca mapę numer → produkt (ostatni z numerem) dla osprzętu — wspólna numeracja maszyn i osprzętu.
 */
export function linkShopProducts(machines, products) {
  const byProductNumber = new Map();
  if (!products.length) return byProductNumber;

  const byNumber = new Map();
  for (const m of machines) if (m.no) byNumber.set(String(m.no), m);

  const guessable = [];
  for (const product of products) {
    const tagged = byNumber.get(numberOf(product.no));
    if (tagged) {
      if (!tagged.shop) tagged.shop = product;
    } else {
      guessable.push(product);
    }
  }
  for (const product of products) {
    const key = numberOf(product.no);
    if (key) byProductNumber.set(key, product);
  }
  if (!guessable.length) return byProductNumber;

  const keys = [];
  for (const m of machines) {
    if (m.shop) continue;
    for (const code of m.codes) {
      const key = slug(code);
      if (key.length >= 5 && /[a-z]/.test(key)) keys.push({ key, machine: m });
    }
  }
  keys.sort((a, b) => b.key.length - a.key.length);

  for (const product of guessable) {
    const title = slug(product.title);
    if (PART_WORDS.some((word) => title.includes(word))) continue;
    if (!MACHINE_WORDS.some((word) => title.includes(word))) continue;
    const hit = keys.find((k) => title.includes(k.key));
    if (hit && !hit.machine.shop) hit.machine.shop = product;
  }
  return byProductNumber;
}

/**
 * Do podglądu kategorii: najmniejsza, środkowa i największa maszyna (≤ 3 maszyny: wszystkie).
 * Skala: wydajność m²/h, potem zbiornik × 8, potem masa.
 */
export function pickPreviewMachines(catalog, categoryId) {
  const rank = (m) => m.specs.perf || (m.specs.tankSolution ? m.specs.tankSolution * 8 : m.specs.weight || 0);
  const list = catalog.machines.filter((m) => m.cats.includes(categoryId)).sort((a, b) => rank(a) - rank(b));
  if (list.length <= 3) return list;
  return [list[0], list[Math.floor((list.length - 1) / 2)], list[list.length - 1]];
}

/** Identyfikator pierwszego pytania ścieżki kategorii (parametr `krok`) albo 'results'. */
export function firstStep(catalog, categoryId) {
  const step = ((catalog.flow && catalog.flow[categoryId]) || [])[0];
  return step ? step.id : 'results';
}

/** Adres doradcy z parametrami w podanej kolejności, np. `{ cat, krok }`, `{ d }`, `{ k }`; puste pomijane. */
export function advisorUrl(params = {}, page = DEFAULT_PAGE) {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value != null && value !== '') query.set(key, value);
  const qs = query.toString();
  return qs ? `${page}${page.includes('?') ? '&' : '?'}${qs}` : page;
}

/* ---- Kod zestawu SE-XXXX (FR-18-34/35): varint numeru maszyny, odcisk listy, maska, bajt kontrolny, Crockford Base32. ---- */

const CODE_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

function base32(bytes) {
  let bits = 0;
  let value = 0;
  let out = '';
  for (const byte of bytes) {
    value = (value << 8) | (byte & 255);
    bits += 8;
    while (bits >= 5) {
      out += CODE_ALPHABET[(value >>> (bits - 5)) & 31];
      bits -= 5;
    }
  }
  if (bits > 0) out += CODE_ALPHABET[(value << (5 - bits)) & 31];
  return out;
}

// Pomyłki przy dyktowaniu: I/L → 1, O → 0, U → V; prefiks SE i separatory pomijane.
function unbase32(text) {
  const clean = String(text || '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/^SE/, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0')
    .replace(/U/g, 'V');
  if (!clean) return null;
  let bits = 0;
  let value = 0;
  const out = [];
  for (const char of clean) {
    const index = CODE_ALPHABET.indexOf(char);
    if (index < 0) return null;
    value = (value << 5) | index;
    bits += 5;
    if (bits >= 8) {
      out.push((value >>> (bits - 8)) & 255);
      bits -= 8;
    }
  }
  return out;
}

function checkByte(bytes) {
  let sum = 7;
  for (const byte of bytes) sum = (sum * 33 + byte) % 251;
  return sum;
}

/** Kody osprzętu w masce kodu: wszystkie grupy poza „W zestawie”, istniejące w `items` (także ukryte). */
export function kitOptions(accessories, machineId) {
  const groups = (accessories && accessories.machines && accessories.machines[machineId]) || [];
  const out = [];
  for (const group of groups) {
    if (/w zestawie/i.test(group.t)) continue;
    for (const code of group.c) if (accessories.items[code]) out.push(code);
  }
  return out;
}

/** Odcisk listy osprzętu — zmiana listy unieważnia stare maski (kod „nieaktualny”). */
export function kitFingerprint(options) {
  let sum = 0;
  options.join('|').split('').forEach((char, i) => {
    sum = (sum * 31 + char.charCodeAt(0) + i) % 251;
  });
  return sum;
}

/** Kod zestawu, np. 'SE-F931-5R07-W8'; '' gdy maszyna nie ma numeru. `picked` — Set kodów osprzętu. */
export function encodeKitCode(no, options, picked) {
  if (!no) return '';
  const bytes = [];
  let rest = no;
  while (rest > 127) {
    bytes.push((rest & 127) | 128);
    rest >>= 7;
  }
  bytes.push(rest);
  bytes.push(kitFingerprint(options));

  const mask = [];
  options.forEach((code, i) => {
    if (picked.has(code)) mask[i >> 3] = (mask[i >> 3] || 0) | (1 << (i & 7));
  });
  let last = mask.length;
  while (last > 0 && !mask[last - 1]) last -= 1;
  for (let i = 0; i < last; i += 1) bytes.push(mask[i] || 0);

  const raw = base32(bytes.concat(checkByte(bytes)));
  return `SE-${(raw.match(/.{1,4}/g) || []).join('-')}`;
}

/**
 * Odczyt kodu: `{ machineId, picked: Set, stale }` albo null (uszkodzony, zła suma, brak maszyny o tym numerze
 * wśród `machines`). `stale` — lista osprzętu zmieniła się od wydania kodu.
 */
export function decodeKitCode(text, machines, accessories) {
  const bytes = unbase32(text);
  if (!bytes || bytes.length < 3) return null;
  const body = bytes.slice(0, -1);
  if (bytes[bytes.length - 1] !== checkByte(body)) return null;

  let no = 0;
  let shift = 0;
  let i = 0;
  while (i < body.length) {
    const byte = body[i];
    i += 1;
    no |= (byte & 127) << shift;
    if (!(byte & 128)) break;
    shift += 7;
  }

  const machine = machines.find((m) => m.no === no);
  if (!machine || i >= body.length) return null;
  const fingerprint = body[i];
  i += 1;

  const options = kitOptions(accessories, machine.id);
  const picked = new Set();
  for (let bit = 0; bit < options.length; bit += 1) {
    const byte = body[i + (bit >> 3)];
    if (byte && byte & (1 << (bit & 7))) picked.add(options[bit]);
  }
  return { machineId: machine.id, picked, stale: fingerprint !== kitFingerprint(options) };
}
